import { bboxCenter } from "utils/math/bboxCenter";
import { collectGreedyMatches } from "./collectGreedyMatches";
import { euclideanDistance, groupByPage } from "./matching";
import type { GroupedSmartSurveyorElement } from "components/SmartSurveyor/ReviewPage/types";
import type { DiffSide } from "../computeQuantityDiff";
import type {
  MatchCandidate,
  MatchMarkers,
  VisualDiffMarker,
  VisualDiffPoint,
  VisualDiffStatus,
} from "./types";

const CELL_NEIGHBOR_OFFSETS = [-1, 0, 1];

const toVisualDiffMarker = (
  marker: GroupedSmartSurveyorElement,
  side: DiffSide,
  status: VisualDiffStatus,
  typeKey: string,
): VisualDiffMarker => ({
  ...marker,
  id: `${side}:${marker.id}`,
  side,
  status,
  typeKey,
});

// Cell size equals the tolerance, so every pair within tolerance shares a cell
// or sits in an adjacent one — the 3x3 neighborhood scan misses nothing.
const buildCellIndex = (
  centers: VisualDiffPoint[],
  cellSize: number,
): Map<string, number[]> => {
  const indicesByCell = new Map<string, number[]>();
  centers.forEach((center, index) => {
    const cellKey = `${Math.floor(center.x / cellSize)}:${Math.floor(center.y / cellSize)}`;
    const cellIndices = indicesByCell.get(cellKey);
    if (cellIndices === undefined) {
      indicesByCell.set(cellKey, [index]);
      return;
    }
    cellIndices.push(index);
  });
  return indicesByCell;
};

export const matchMarkers: MatchMarkers = ({
  beforeMarkers,
  afterMarkers,
  tolerancePdfPoints,
  typeKey,
}) => {
  const results: VisualDiffMarker[] = [];
  const beforeByPage = groupByPage(beforeMarkers, (marker) => marker.page ?? 0);
  const afterByPage = groupByPage(afterMarkers, (marker) => marker.page ?? 0);
  const pageIndices = new Set([...beforeByPage.keys(), ...afterByPage.keys()]);
  const cellSize = tolerancePdfPoints > 0 ? tolerancePdfPoints : 1;

  for (const pageIndex of pageIndices) {
    const beforeBucket = beforeByPage.get(pageIndex) ?? [];
    const afterBucket = afterByPage.get(pageIndex) ?? [];
    const beforeCenters = beforeBucket.map((marker) =>
      bboxCenter({ bbox: marker.bbox }),
    );
    const afterCenters = afterBucket.map((marker) =>
      bboxCenter({ bbox: marker.bbox }),
    );
    const afterIndicesByCell = buildCellIndex(afterCenters, cellSize);

    const candidates: MatchCandidate[] = [];
    beforeCenters.forEach((beforeCenter, beforeIndex) => {
      const cellX = Math.floor(beforeCenter.x / cellSize);
      const cellY = Math.floor(beforeCenter.y / cellSize);
      for (const offsetX of CELL_NEIGHBOR_OFFSETS) {
        for (const offsetY of CELL_NEIGHBOR_OFFSETS) {
          const cellIndices = afterIndicesByCell.get(
            `${cellX + offsetX}:${cellY + offsetY}`,
          );
          if (cellIndices === undefined) continue;
          for (const afterIndex of cellIndices) {
            const distance = euclideanDistance(
              beforeCenter,
              afterCenters[afterIndex],
            );
            if (distance > tolerancePdfPoints) continue;
            candidates.push({
              beforeIndex,
              afterIndex,
              distance,
              beforeId: beforeBucket[beforeIndex].id,
              afterId: afterBucket[afterIndex].id,
            });
          }
        }
      }
    });

    const { pairs, matchedBeforeIndices, matchedAfterIndices } =
      collectGreedyMatches(candidates);

    for (const pair of pairs) {
      const beforeMarker = beforeBucket[pair.beforeIndex];
      const afterMarker = afterBucket[pair.afterIndex];
      const status: VisualDiffStatus =
        beforeMarker.multiplier === afterMarker.multiplier
          ? "unchanged"
          : "changed";
      results.push(toVisualDiffMarker(afterMarker, "after", status, typeKey));
    }
    afterBucket.forEach((marker, index) => {
      if (matchedAfterIndices.has(index)) return;
      results.push(toVisualDiffMarker(marker, "after", "added", typeKey));
    });
    beforeBucket.forEach((marker, index) => {
      if (matchedBeforeIndices.has(index)) return;
      results.push(toVisualDiffMarker(marker, "before", "removed", typeKey));
    });
  }

  return results;
};
